import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { SERVICE_TYPES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", suburb: "", service_type: "", message: "" };

const inputCls =
  "w-full bg-transparent border-b border-charcoal/20 py-3 text-charcoal placeholder:text-taupe/60 font-light focus:border-sand focus:outline-none transition-colors";
const labelCls = "block eyebrow !text-charcoal/70 mb-2";

export default function EnquiryForm({ defaultService = "", dark = false }) {
  const [form, setForm] = useState({ ...EMPTY, service_type: defaultService });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.suburb || !form.service_type) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "quote_submitted", {
          service_type: form.service_type,
          suburb: form.suburb,
        });
      }
      setDone(true);
      toast.success("Enquiry received — we'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please call us on 0436 376 922.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className={`p-10 border ${dark ? "border-white/15 text-white" : "border-black/10 text-charcoal"}`}>
        <CheckCircle2 className="h-12 w-12 text-sand mb-6" strokeWidth={1.2} />
        <h3 className="font-display text-3xl font-extrabold mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className={`font-light ${dark ? "text-white/70" : "text-taupe"}`}>
          Your enquiry has reached the CEMTEX studio. We'll provide a comprehensive, obligation-free quote within one business day.
        </p>
        <button
          data-testid="enquiry-reset"
          onClick={() => { setForm({ ...EMPTY, service_type: defaultService }); setDone(false); }}
          className="mt-8 text-xs uppercase tracking-[0.2em] text-sand hover:underline"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={submit} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input data-testid="quote-input-name" className={inputCls} placeholder="Your name" value={form.name} onChange={update("name")} />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input data-testid="quote-input-phone" className={inputCls} placeholder="0436 376 922" value={form.phone} onChange={update("phone")} />
        </div>
        <div>
          <label className={labelCls}>Email Address *</label>
          <input data-testid="quote-input-email" type="email" className={inputCls} placeholder="your@email.com" value={form.email} onChange={update("email")} />
        </div>
        <div>
          <label className={labelCls}>Suburb / Location *</label>
          <input data-testid="quote-input-suburb" className={inputCls} placeholder="e.g. Subiaco, Perth WA" value={form.suburb} onChange={update("suburb")} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Service Type *</label>
        <select data-testid="quote-input-service" className={`${inputCls} cursor-pointer`} value={form.service_type} onChange={update("service_type")}>
          <option value="" disabled>Select a service</option>
          {SERVICE_TYPES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>Project Description</label>
        <textarea data-testid="quote-input-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your project — what you're looking to achieve, approximate area, and timeframe." value={form.message} onChange={update("message")} />
      </div>

      <button
        data-testid="quote-submit"
        type="submit"
        disabled={submitting}
        className="btn-sweep inline-flex items-center gap-3 bg-charcoal text-white hover:text-charcoal px-10 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 disabled:opacity-60"
      >
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting</> : "Submit Enquiry"}
      </button>
    </form>
  );
}
